<template lang="pug">
v-fade-transition(mode="in-out" class="tw-p-0 tw-m-0")
  v-container(fluid fill-height class="tw-p-0 tw-h-screen tw-overflow-hidden")
    v-row(align="center" justify="space-between")
      v-col(cols="6" class="hidden-sm-and-down")
        v-card(tile :img="image" class="tw-h-screen tw-p-0 tw-m-0 tw-border-0")
      v-col(lg="6" cols="12" class="")
        v-row(fill-height align="center" justify="center")
          v-col(lg="8" cols="10")
            v-row(align="center" justify="center")
              v-col(cols="auto")
                v-img(src="@/assets/images/kubiqa-logo.svg" width="300px")

            v-row(align="center" justify="center")
              v-col(class="text-center")
                v-form(ref="form" v-model="valid" lazy-validation @keyup.native.enter="valid && submitLoginForm($event)")
                  v-alert(v-if="loginErrorMessage" type="error" text role="alert" class="tw-mb-4") {{ loginErrorMessage }}
                  v-alert(v-if="googleMessage" type="info" text role="status" class="tw-mb-4") {{ googleMessage }}
                  template(v-if="googleTwoFactorRequired")
                    v-alert(v-if="twoFactorError" type="error" text role="alert" class="tw-mb-4") {{ twoFactorError }}
                    v-text-field(
                      v-if="twoFactorMode === 'code'"
                      v-model="twoFactorCode"
                      :rules="twoFactorRules"
                      label="Authentication code"
                      autocomplete="one-time-code"
                      inputmode="numeric"
                      color="blue darken-4"
                      required
                      autofocus
                    )
                    v-text-field(
                      v-else
                      v-model="twoFactorCode"
                      :rules="twoFactorRules"
                      label="Recovery code"
                      autocomplete="one-time-code"
                      color="blue darken-4"
                      required
                      autofocus
                    )
                    v-btn(
                      color="rgba(94,129,244)"
                      elevation="0"
                      rounded
                      :loading="loading"
                      @click="submitGoogleTwoFactor"
                      class="tw-w-2/3"
                    )
                      span(class="tw-font-bold") Verify and continue
                    v-btn(text :disabled="loading" @click="toggleTwoFactorMode" class="tw-mt-3") {{ twoFactorMode === 'code' ? 'Use a recovery code' : 'Use an authentication code' }}
                  template(v-else)
                    v-text-field(
                      v-model="email"
                      :rules="emailRules"
                      :label="$t('Common.email')"
                      color="blue darken-4"
                      required
                    )
                    v-text-field(
                      v-model="password"
                      :rules="passwordRules"
                      :type="'password'"
                      name="input-10-1"
                      :label="$t('Common.password')"
                      color="blue darken-4"
                      required
                    )
                    v-btn(
                      color="rgba(94,129,244)"
                      elevation="0"
                      rounded
                      :loading="loading"
                      @click="login"
                      class=" tw-w-2/3 "
                    )
                      span(class="tw-font-bold") {{ $t('Common.login') }}
                    div(class="tw-my-5 tw-text-center" role="separator" aria-label="or") Or
                    v-btn(
                      color="blue-grey darken-4"
                      outlined
                      rounded
                      :disabled="loading"
                      aria-label="Continue with Google"
                      @click="beginGoogleSignIn"
                      height="48"
                      class="tw-w-2/3 google-login-btn"
                    )
                      v-icon(color="#4285F4" class="google-login-icon") mdi-google
                      span(class="tw-font-medium") Continue with Google

                v-row(align="center" justify="center" class="tw-mt-3")
                  router-link(
                    :to="{ name: 'ForgotPassword' }"
                    class="tw-text-sm tw-font-medium tw-text-blue-700 tw-underline"
                  ) {{ $t('Common.forgotPassword') }}

        //- v-row
        //-   v-snackbar(v-model="error" color="error")
        //-     span() {{ error }}
</template>

<script>
import { getError } from '@/utils/helpers';
import AuthService from '@/services/AuthService';
import { googleAuthorizationUrl, googleCallbackOutcome } from '@/features/google-oauth';
export default {
  name: 'Login',
  data() {
    return {
      valid: true,
      error: false,
      loading: false,
      error_message: '',
      loginErrorMessage: '',
      googleMessage: '',
      googleTwoFactorRequired: false,
      pendingEmailRequestId: 0,
      twoFactorCode: '',
      twoFactorError: '',
      twoFactorMode: 'code',
      twoFactorRules: [(v) => !!(v && v.trim()) || 'Please enter your authentication or recovery code'],
      password: '',
      passwordRules: [(v) => !!v || 'Please enter your password'],
      email: '',
      emailRules: [(v) => !!v || 'Please enter your user'],
      image: require('@/assets/images/auth/real-estate-login.jpg'),
    };
  },
  created() {
    this.applyGoogleCallbackOutcome();
  },
  methods: {
    beginGoogleSignIn() {
      const authorizationUrl = googleAuthorizationUrl();

      if (!authorizationUrl) {
        this.error = Error('Google sign-in is temporarily unavailable. Please sign in with your password.');
        return;
      }

      window.location.assign(authorizationUrl);
    },
    async completeGoogleSignIn() {
      this.loading = true;
      this.error = null;

      try {
        const user = await this.$store.dispatch('auth/getAuthUser');
        if (!user) {
          throw Error('Unable to fetch user after Google sign-in, check your API settings.');
        }

        await this.$store.dispatch('auth/setGuest', { value: 'isNotGuest' });
        this.$router.push('/dashboard');
      } catch (error) {
        this.error = error;
        this.loading = false;
      }
    },
    async applyGoogleCallbackOutcome() {
      const outcome = googleCallbackOutcome(this.$route && this.$route.query);

      if (outcome === 'success') {
        this.completeGoogleSignIn();
      }

      if (outcome === 'password_required') {
        await this.loadPendingGoogleEmail();
      }

      if (outcome === 'two_factor_required') {
        this.googleTwoFactorRequired = true;
        this.twoFactorError = '';
        this.googleMessage = 'Enter the authentication code for your Kubiqa account to finish signing in.';
      }

      if (outcome === 'failed') {
        this.error = Error('Google sign-in could not be completed. Please try again or sign in with your password.');
      }
    },
    async loadPendingGoogleEmail() {
      const requestId = this.pendingEmailRequestId + 1;
      this.pendingEmailRequestId = requestId;
      this.googleMessage = 'A Kubiqa account already exists for the Google email you chose. Sign in with that account password to continue.';

      try {
        const response = await AuthService.getPendingGoogleEmail();
        const email = response && response.data && response.data.email;

        if (!this.canApplyPendingGoogleEmail(requestId) || !this.isEmail(email)) {
          return;
        }

        this.email = email;
        this.googleMessage = `A Kubiqa account already exists for ${email}. Enter that account password to continue.`;
      } catch (error) {
        // Missing or expired handoffs are intentionally non-identifying. The
        // visitor can still use the normal password form without being signed in.
      }
    },
    canApplyPendingGoogleEmail(requestId) {
      return requestId === this.pendingEmailRequestId
        && !this.$isDestroyed
        && !this._isBeingDestroyed;
    },
    isEmail(value) {
      return typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    },
    submitLoginForm(event) {
      return this.googleTwoFactorRequired ? this.submitGoogleTwoFactor() : this.login(event);
    },
    toggleTwoFactorMode() {
      this.twoFactorMode = this.twoFactorMode === 'code' ? 'recovery' : 'code';
      this.twoFactorCode = '';
      this.twoFactorError = '';
    },
    async submitGoogleTwoFactor() {
      if (!this.googleTwoFactorRequired || this.loading) {
        return;
      }

      const proof = typeof this.twoFactorCode === 'string' ? this.twoFactorCode.trim() : '';
      if (!proof) {
        this.twoFactorError = 'Enter an authentication code or recovery code to continue.';
        return;
      }

      const payload = this.twoFactorMode === 'recovery' ? { recovery_code: proof } : { code: proof };

      this.loading = true;
      this.error = null;
      this.twoFactorError = '';

      try {
        await AuthService.twoFactorChallenge(payload);
        this.twoFactorCode = '';
        await this.completeGoogleSignIn();
      } catch (error) {
        this.twoFactorCode = '';
        this.twoFactorError = 'The authentication or recovery code could not be confirmed. Try again.';
        this.loading = false;
      }
    },
    validate() {
      if (this.$refs.form.validate()) {
        this.snackbar = true;
      }
    },
    reset() {
      this.$refs.form.reset();
    },
    resetValidation() {
      this.$refs.form.resetValidation();
    },
    async login() {
      if (this.loading) {
        return;
      }

      this.loading = true;
      this.loginErrorMessage = '';

      try {
        await AuthService.login({
          email: this.email,
          password: this.password,
        });

        const user = await this.$store.dispatch('auth/getAuthUser');
        if (!user) {
          this.loginErrorMessage = 'Sign-in is temporarily unavailable. Please wait a moment and try again.';
          return;
        }

        await this.$store.dispatch('auth/setGuest', { value: 'isNotGuest' });
        this.$router.push('/dashboard');
        pendo.initialize({
          visitor: {
            id: this.email,
          },
          account: {
            id: 'ACCOUNT-UNIQUE-ID',
          },
        });
      } catch (error) {
        this.error = error;
        const status = error && error.response && error.response.status;
        this.loginErrorMessage = status === 429
          ? 'Too many requests. Please wait a moment and try again.'
          : 'Sign-in could not be completed. Please check your details and try again.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.google-login-btn {
  color: #263238 !important;
  border-color: #cbd5e1 !important;
  background-color: #fff !important;
}

.google-login-icon {
  margin-right: 8px;
}

@media (max-width: 600px) {
  .google-login-btn {
    width: 100% !important;
  }
}
</style>
